/* storybook-check-ignore */
import { Text, PresentationalRadioGroup, Box, Button, Flex } from '@opendoor/bricks/core';
import { ExclusiveTourRequestTimeSlot, arrayIncludeSlot } from './util';
import { useState, useMemo } from 'react';
import ReserveATourSlotContent, { ReserveATourSlotContentProps } from './ReserveATourSlotContent';
import { css } from '@emotion/react';
import { Listing } from 'declarations/exclusives/listing';
import ReserveATourSelectedSpotConfirmationModal from './ReserveATourSelectedSpotConfirmationModal';
import { useRequestATour } from './useRequestATour';

export default function ReserveATourSelectSingleSlot({
  closestSlots,
  instantSlots,
  onCompleted,
  reportEvent,
  listing,
  hasTourRequest,
  requestedTourSlots = [],
}: {
  closestSlots: ReserveATourSlotContentProps[];
  instantSlots: ExclusiveTourRequestTimeSlot[];
  onCompleted: (_: {
    slot?: ExclusiveTourRequestTimeSlot;
    contextualData: { buttonText: string; origin: string };
  }) => void;
  reportEvent: ReturnType<typeof useRequestATour>['reportEvent'];
  listing: Listing;
  hasTourRequest: boolean | undefined;
  requestedTourSlots: ExclusiveTourRequestTimeSlot[] | undefined;
}) {
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [selectedSlotId, setSelectedSpotId] = useState<number | undefined>(undefined);
  const selectedSlot =
    selectedSlotId !== undefined ? closestSlots[selectedSlotId]?.slot : undefined;
  const hasSomeInstantSpot = closestSlots.some(
    ({ slot }) => slot && arrayIncludeSlot(instantSlots, slot),
  );
  const buttonText =
    selectedSlot && instantSlots.includes(selectedSlot)
      ? 'Reserve tour slot'
      : selectedSlotId === closestSlots.length
      ? 'Request new time for tour'
      : 'Request tour slot';
  const selectedSlotAlreadyReserved =
    selectedSlot && arrayIncludeSlot(requestedTourSlots, selectedSlot);
  const onReserveATourClick = () => {
    if (hasSomeInstantSpot && selectedSlot) {
      reportEvent('tour-request', {
        buttonText,
        slot: selectedSlot,
        origin: 'request-a-tour-buttons',
      });
      setConfirmationModalIsOpen(true);
    } else {
      onCompleted({
        slot: selectedSlot,
        contextualData: { buttonText, origin: 'reserve-a-tour-select-single-slot' },
      });
    }
  };
  const options = useMemo(() => {
    const options = closestSlots.map((props, i) => ({
      label: <ReserveATourSlotContent {...props} listing={listing} />,
      value: i.toString(),
    }));
    if (!hasTourRequest) {
      options.push({
        value: closestSlots.length.toString(),
        label: <ReserveATourSlotContent slot={undefined} isPrimary={false} listing={listing} />,
      });
    }
    return options;
  }, [closestSlots, hasTourRequest]);
  return (
    <>
      {hasSomeInstantSpot && <Text>Reserve a slot in an existing tour or request a new time.</Text>}
      <Box width="100%" css={radioGroupCSSOverride}>
        <PresentationalRadioGroup
          hasContainer
          options={options}
          onClick={(val) => setSelectedSpotId(Number(val))}
          selectedValue={selectedSlotId !== undefined ? selectedSlotId.toString() : undefined}
          analyticsName="cosmos-exclusives-reserve-a-tour-mobile-select-slot-radio-group"
          name="Reserve a tour radio group"
        />
      </Box>
      <Flex flexDirection="column" gridGap="0.75rem">
        <Button
          aria-label={null}
          analyticsName="cosmos-exclusives-reserve-a-tour-modal-request-times-button"
          onClick={onReserveATourClick}
          disabled={selectedSlotId === undefined || selectedSlotAlreadyReserved}
          width="100%"
          flex={1}
        >
          {selectedSlot && instantSlots.includes(selectedSlot)
            ? selectedSlotAlreadyReserved
              ? 'Spot reserved'
              : 'Reserve tour slot'
            : selectedSlotId === closestSlots.length
            ? 'Request new time for tour'
            : 'Request tour slot'}
        </Button>
        <Text color="neutrals90" fontSize="s0" textAlign="center">
          No agent needed. Tour at your own pace.
        </Text>
      </Flex>
      {confirmationModalIsOpen && hasSomeInstantSpot && selectedSlot && (
        <ReserveATourSelectedSpotConfirmationModal
          onClose={() => {
            setConfirmationModalIsOpen(false);
            reportEvent('request-a-tour-select-single-spot', {
              buttonText: 'cancel',
              origin: 'reserve-a-tour-selected-spot-confirmation-modal',
            });
          }}
          onConfirm={() => {
            setConfirmationModalIsOpen(false);
            onCompleted({
              slot: selectedSlot,
              contextualData: {
                buttonText,
                origin: 'reserve-a-tour-selected-spot-confirmation-modal',
              },
            });
          }}
          listing={listing}
          slot={selectedSlot}
        />
      )}
    </>
  );
}

const radioGroupCSSOverride = css`
  div[role='radiogroup'] > div {
    padding: 0.75rem 1rem !important;
  }
`;
